/**
 * Fase 2 (W32): a abertura do login, o pós-login e a saída no jeito do ERP antigo (foto do farol em preto e branco, a cor
 * surgindo num círculo e o splash com a logo), com tempos mais curtos e um teto. Regras puras, testadas em
 * `tests/w32-identidade.test.ts`; a tela está em `paginas/fundo-publico.tsx`.
 *
 * Tempos (o antigo segurava 2 s com o cartão oculto e mais 1 s de revelação):
 * - abertura: splash 600 ms, a cor se abre em 900 ms e o formulário entra 250 ms depois do fim do splash, em 450 ms. O
 *   formulário deixa de ser `inert` aos 600 ms, aparece entre 850 e 1.300 ms e tudo para aos 1.500 ms (`TETO_ABERTURA_MS`). Qualquer tecla, clique ou toque
 *   durante o splash pula direto para o formulário: quem tem pressa não espera a animação.
 * - pós-login: 600 ms com a cor se fechando e a logo, em paralelo com a carga da sessão (a navegação espera as duas).
 * - saída: 1.100 ms de "Saindo" com a cor se fechando e, depois, a despedida em 450 ms.
 * - movimento reduzido (`prefers-reduced-motion`): sem splash na abertura e tudo vira fade de 200 ms, sem círculo, pulso ou
 *   deslize.
 */

export type Fase = 'carregando' | 'revelando' | 'pronto' | 'fechando' | 'saindo' | 'despedida';

export interface Tempos {
  /** Splash da abertura (logo pulsando, foto em preto e branco). */
  splash: number;
  /** A cor se abrindo em círculo. */
  revelar: number;
  /** A cor se fechando (pós-login e saída). */
  fechar: number;
  /** Espera do formulário depois do fim do splash (a cor começa a abrir antes). */
  atrasoCartao: number;
  /** Entrada do formulário (opacidade e deslize de 12 px). */
  cartao: number;
  /** Saída do splash. */
  sumir: number;
  /** Mínimo do splash do pós-login antes de navegar. */
  posLogin: number;
  /** "Saindo" antes da despedida. */
  saindo: number;
}

export const TEMPOS: Tempos = { splash: 600, revelar: 900, fechar: 600, atrasoCartao: 250, cartao: 450, sumir: 300, posLogin: 600, saindo: 1100 };
/** Movimento reduzido: fades de 200 ms e nenhuma espera de splash na abertura. */
export const TEMPOS_REDUZIDOS: Tempos = { splash: 0, revelar: 200, fechar: 200, atrasoCartao: 0, cartao: 200, sumir: 200, posLogin: 200, saindo: 200 };
/** Teto da abertura: nada se mexe depois disso. */
export const TETO_ABERTURA_MS = 1500;

export const tempos = (reduzido: boolean): Tempos => (reduzido ? TEMPOS_REDUZIDOS : TEMPOS);

/** Duração da fase até a próxima automática (`null`: a fase não termina sozinha). */
export function duracaoDaFase(fase: Fase, t: Tempos): number | null {
  if (fase === 'carregando') return t.splash;
  if (fase === 'revelando') return Math.max(t.revelar, t.atrasoCartao + t.cartao, t.sumir);
  if (fase === 'saindo') return t.saindo;
  return null;
}

/** Próxima fase automática. A despedida termina parada (foto em preto e branco); o login a abre de novo. */
export function proximaFase(fase: Fase): Fase | null {
  if (fase === 'carregando') return 'revelando';
  if (fase === 'revelando') return 'pronto';
  if (fase === 'saindo') return 'despedida';
  return null;
}

/**
 * Fase da primeira pintura: a abertura toca só na primeira vez que o login aparece neste carregamento da página (trocar
 * entre login, recuperar senha e voltar não repete); com movimento reduzido, começa já revelando (sem splash).
 */
export function faseInicial(caminho: string, reduzido: boolean, aberturaTocou: boolean): Fase {
  if (caminho === '/saida') return 'saindo';
  if (caminho === '/login' && !aberturaTocou) return reduzido ? 'revelando' : 'carregando';
  return 'pronto';
}

/** Fase ao trocar de página pública: saindo na `/saida`; depois da despedida, a cor volta a abrir no login. */
export function faseAoNavegar(caminho: string, atual: Fase): Fase {
  if (caminho === '/saida') return atual === 'despedida' ? 'despedida' : 'saindo';
  if (atual === 'despedida' || atual === 'saindo') return 'revelando';
  return atual;
}

export type Splash = { tipo: 'abertura' | 'entrando' | 'saindo'; sumindo: boolean } | null;

/** Splash da fase. `anterior` diz se havia splash antes (a revelação depois do pulo ou do movimento reduzido não tem). */
export function splashDaFase(fase: Fase, anterior: Fase | null): Splash {
  if (fase === 'carregando') return { tipo: 'abertura', sumindo: false };
  if (fase === 'fechando') return { tipo: 'entrando', sumindo: false };
  if (fase === 'saindo') return { tipo: 'saindo', sumindo: false };
  if (fase === 'revelando' && anterior === 'carregando') return { tipo: 'abertura', sumindo: true };
  if (fase === 'despedida' && anterior === 'saindo') return { tipo: 'saindo', sumindo: true };
  return null;
}

/** O formulário fica fora da tela (e `inert`) enquanto o splash cobre. */
export const formularioOculto = (fase: Fase) => fase === 'carregando' || fase === 'saindo';

interface Conexao {
  saveData?: boolean;
  effectiveType?: string;
}

/** Foto do farol só com conexão boa: Save-Data ou 2G/3G ficam com a cor sólida de reserva (como no antigo). */
export function fotoPermitida(conexao: Conexao | undefined | null): boolean {
  if (!conexao) return true;
  return !(conexao.saveData === true || ['slow-2g', '2g', '3g'].includes(conexao.effectiveType ?? ''));
}
